import { useState } from "react";
import { Settings, Sun, Moon, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useStahliaTweaks, type Theme, type Accent, type Density } from "@/lib/tweaks";
import { cn } from "@/lib/utils";

function ToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { id: T; label: string; icon?: React.ReactNode }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-ink-2">{label}</div>
      <div className="flex gap-1 p-1 rounded-md bg-panel-2 border border-line">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            className={cn(
              "flex-1 px-3 py-1.5 text-sm rounded transition-colors flex items-center justify-center gap-1.5",
              value === opt.id
                ? "bg-background text-ink shadow-sm"
                : "text-ink-2 hover:text-ink",
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TweaksPanel() {
  const [open, setOpen] = useState(false);
  const { tweaks, update } = useStahliaTweaks();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-4 right-4 rounded-full shadow-[var(--shadow-pop)] z-50"
          aria-label="Einstellungen"
        >
          <Settings className="size-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-80">
        <SheetHeader>
          <SheetTitle>Anzeige-Einstellungen</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          <ToggleGroup<Theme>
            label="Theme"
            value={tweaks.theme}
            onChange={(v) => update({ theme: v })}
            options={[
              { id: "light", label: "Hell", icon: <Sun className="size-3.5" /> },
              { id: "dark", label: "Dunkel", icon: <Moon className="size-3.5" /> },
            ]}
          />
          <ToggleGroup<Accent>
            label="Akzent"
            value={tweaks.accent}
            onChange={(v) => update({ accent: v })}
            options={[
              {
                id: "blue",
                label: "Blau",
                icon: <Circle className="size-3.5 fill-[#0288d1] stroke-none" />,
              },
              {
                id: "coral",
                label: "Coral",
                icon: <Circle className="size-3.5 fill-[#fe877b] stroke-none" />,
              },
              {
                id: "orange",
                label: "Orange",
                icon: <Circle className="size-3.5 fill-[#ff9900] stroke-none" />,
              },
            ]}
          />
          <ToggleGroup<Density>
            label="Dichte"
            value={tweaks.density}
            onChange={(v) => update({ density: v })}
            options={[
              { id: "comfortable", label: "Komfort" },
              { id: "compact", label: "Kompakt" },
            ]}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
